import { useState } from "react";
import { Plus, Pencil, Trash2, GripVertical, Image as ImageIcon, ArrowUp, ArrowDown } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useRestaurantBanners, RestaurantBanner } from "@/hooks/useRestaurantBanners";
import { BannerFormDialog } from "./BannerFormDialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function TabletBannersManager() {
  const { activeRestaurant, refetch: refetchRestaurant } = useRestaurantContext();
  const { banners, isLoading, createBanner, updateBanner, deleteBanner, reorderBanners, refetch } = useRestaurantBanners(activeRestaurant?.id);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<RestaurantBanner | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isSavingInterval, setIsSavingInterval] = useState(false);
  
  const intervalSeconds = (activeRestaurant as any)?.screensaver_interval_seconds || 10;

  const handleIntervalChange = async (value: string) => {
    if (!activeRestaurant) return;
    setIsSavingInterval(true);
    
    const { error } = await supabase
      .from("restaurants")
      .update({ screensaver_interval_seconds: parseInt(value) })
      .eq("id", activeRestaurant.id);
    
    if (error) {
      toast.error("Erro ao salvar intervalo");
    } else {
      toast.success("Intervalo atualizado");
      refetchRestaurant();
    }
    setIsSavingInterval(false);
  };

  const handleOpenCreate = () => {
    setEditingBanner(null);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (banner: RestaurantBanner) => {
    setEditingBanner(banner);
    setIsDialogOpen(true);
  };

  const handleToggleActive = async (banner: RestaurantBanner) => {
    await updateBanner(banner.id, { is_active: !banner.is_active });
    refetch();
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteBanner(deleteId);
      setDeleteId(null);
      refetch();
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newOrder = [...banners];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    const orderedIds = newOrder.map(b => b.id);
    await reorderBanners(orderedIds);
    refetch();
  };

  const handleMoveDown = async (index: number) => {
    if (index === banners.length - 1) return;
    const newOrder = [...banners];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    const orderedIds = newOrder.map(b => b.id);
    await reorderBanners(orderedIds);
    refetch();
  };

  const handleSave = async (data: { title?: string; subtitle?: string; cta_text?: string; is_active: boolean }, imageFile?: File) => {
    if (editingBanner) {
      let image_url = editingBanner.image_url;
      
      if (imageFile) {
        const fileExt = imageFile.name.split(".").pop();
        const fileName = `${activeRestaurant?.id}/banners/${crypto.randomUUID()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from("product-images")
          .upload(fileName, imageFile);
        
        if (uploadError) {
          toast.error("Erro ao fazer upload da imagem");
          return;
        }
        
        const { data: urlData } = supabase.storage
          .from("product-images")
          .getPublicUrl(fileName);
        
        image_url = urlData.publicUrl;
      }
      
      await updateBanner(editingBanner.id, { ...data, image_url });
    } else {
      if (!imageFile) {
        toast.error("Selecione uma imagem para o banner");
        return;
      }
      
      const fileExt = imageFile.name.split(".").pop();
      const fileName = `${activeRestaurant?.id}/banners/${crypto.randomUUID()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from("product-images")
        .upload(fileName, imageFile);
      
      if (uploadError) {
        toast.error("Erro ao fazer upload da imagem");
        return;
      }
      
      const { data: urlData } = supabase.storage
        .from("product-images")
        .getPublicUrl(fileName);
      
      await createBanner({
        ...data,
        image_url: urlData.publicUrl,
      });
    }
    
    setIsDialogOpen(false);
    refetch();
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Banners do Screensaver
          </CardTitle>
          <CardDescription>
            Configure os banners que aparecerão quando o tablet estiver ocioso. Recomendado: 1920x1080 pixels.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Interval Configuration */}
          <div className="flex items-center gap-4">
            <Label>Intervalo entre banners:</Label>
            <Select 
              value={String(intervalSeconds)} 
              onValueChange={handleIntervalChange}
              disabled={isSavingInterval}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 segundos</SelectItem>
                <SelectItem value="10">10 segundos</SelectItem>
                <SelectItem value="15">15 segundos</SelectItem>
                <SelectItem value="20">20 segundos</SelectItem>
                <SelectItem value="30">30 segundos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Banner List */}
          <div className="space-y-3">
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Carregando banners...
              </div>
            ) : banners.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                <ImageIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum banner cadastrado</p>
                <p className="text-sm">Adicione banners promocionais para exibir no tablet</p>
              </div>
            ) : (
              banners.map((banner, index) => (
                <div 
                  key={banner.id} 
                  className="flex items-center gap-4 p-4 border rounded-lg bg-card"
                >
                  <div className="flex flex-col gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === banners.length - 1}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <img 
                    src={banner.image_url} 
                    alt={banner.title || "Banner"}
                    className="w-32 h-20 object-cover rounded"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {banner.title || "Sem título"}
                    </p>
                    {banner.subtitle && (
                      <p className="text-sm text-muted-foreground truncate">
                        {banner.subtitle}
                      </p>
                    )}
                    {banner.cta_text && (
                      <p className="text-xs text-primary mt-1">
                        CTA: {banner.cta_text}
                      </p>
                    )}
                  </div>
                  
                  <Switch 
                    checked={banner.is_active} 
                    onCheckedChange={() => handleToggleActive(banner)}
                  />
                  
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleOpenEdit(banner)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteId(banner.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>

          {/* Add Button */}
          {banners.length < 10 && (
            <Button onClick={handleOpenCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              Adicionar Banner
            </Button>
          )}
          
          {banners.length >= 10 && (
            <p className="text-sm text-muted-foreground">
              Máximo de 10 banners atingido
            </p>
          )}
        </CardContent>
      </Card>

      <BannerFormDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        banner={editingBanner}
        onSave={handleSave}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir banner?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O banner será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
